// next-app/src/lib/validations/survey.ts
import { z } from 'zod';

// ============================================================================
// Survey Validation Schemas
// ============================================================================

/**
 * Personal information schema for surveys.
 */
export const surveyPersonInfoSchema = z.object({
  first_name: z
    .string()
    .min(1, 'First name is required')
    .max(50, 'First name must be 50 characters or less'),
  last_name: z
    .string()
    .min(1, 'Last name is required')
    .max(50, 'Last name must be 50 characters or less'),
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
  phone: z
    .string()
    .min(10, 'Phone number must be at least 10 digits')
    .regex(/^[\d\s()-]+$/, 'Invalid phone number format'),
});

/**
 * Contact/address information schema for surveys.
 */
export const surveyContactInfoSchema = z.object({
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip_code: z
    .string()
    .regex(/^\d{5}(-\d{4})?$/, 'Invalid ZIP code')
    .optional()
    .or(z.literal('')),
});

/**
 * Survey type enum.
 */
export const surveyTypeSchema = z.enum(['pre', 'post', 'basic', 'rsvp', 'check-in']);

/**
 * Complete survey response schema (base for reference).
 */
export const surveyResponseSchema = surveyPersonInfoSchema.merge(surveyContactInfoSchema).extend({
  event_id: z.number(),
  hash: z.string(),
  survey_type: surveyTypeSchema,
  responses: z.record(z.string(), z.unknown()).optional(),
  waiver_accepted: z.boolean().optional(),
  email_opt_in: z.boolean().optional(),
});

/**
 * Pre-drive survey schema with waiver required.
 */
export const preDriveSurveySchema = surveyPersonInfoSchema.merge(surveyContactInfoSchema).extend({
  event_id: z.number(),
  hash: z.string(),
  survey_type: surveyTypeSchema,
  responses: z.record(z.string(), z.unknown()).optional(),
  waiver_accepted: z.boolean(),
  email_opt_in: z.boolean().optional(),
});

/**
 * Post-drive survey schema with vehicles driven.
 */
export const postDriveSurveySchema = surveyPersonInfoSchema.merge(surveyContactInfoSchema).extend({
  event_id: z.number(),
  hash: z.string(),
  survey_type: surveyTypeSchema,
  responses: z.record(z.string(), z.unknown()).optional(),
  vehicles_driven: z.array(z.number()).optional(),
  test_drove: z.boolean().optional(),
  email_opt_in: z.boolean().optional(),
});

/**
 * Basic/RSVP survey schema (simpler requirements).
 */
export const basicSurveySchema = surveyPersonInfoSchema.extend({
  event_id: z.number(),
  hash: z.string(),
  survey_type: surveyTypeSchema,
  email_opt_in: z.boolean().optional(),
});

/**
 * Check-in survey schema.
 */
export const checkInSurveySchema = surveyPersonInfoSchema.merge(surveyContactInfoSchema).extend({
  event_id: z.number(),
  hash: z.string(),
  survey_type: surveyTypeSchema,
  responses: z.record(z.string(), z.unknown()).optional(),
  waiver_accepted: z.boolean(),
  email_opt_in: z.boolean().optional(),
});

// ============================================================================
// Type Exports
// ============================================================================

export type SurveyPersonInfo = z.infer<typeof surveyPersonInfoSchema>;
export type SurveyContactInfo = z.infer<typeof surveyContactInfoSchema>;
export type SurveyResponseForm = z.infer<typeof surveyResponseSchema>;
export type PreDriveSurveyForm = z.infer<typeof preDriveSurveySchema>;
export type PostDriveSurveyForm = z.infer<typeof postDriveSurveySchema>;
export type BasicSurveyForm = z.infer<typeof basicSurveySchema>;
export type CheckInSurveyForm = z.infer<typeof checkInSurveySchema>;
